import { fmt, withUnit } from './format';

export const REPAIR_LIMIT = 10;
export const EQUIPMENT_LIMIT = 10;

const cellValue = (value, column) => {
    if (value == null) return '';
    return column === 'km' ? withUnit(fmt(value), 'km') : String(value);
};

// Repair table (reports read as a table instead of a visit timeline); a column with no value in any row is left out
export const buildRepairs = (repairs, { allRepairs }, t) => {
    const rows = (repairs && repairs.rows) || [];
    if (!rows.length) return null;

    const columns = [
        { k: 'date', width: '120px' },
        { k: 'code', width: '130px', mono: true },
        { k: 'dossier', width: '130px', mono: true },
        { k: 'reason', width: '120px', mono: true, sub: 'reasonLabel' },
        { k: 'op', width: 'minmax(220px, 1fr)', src: true, sub: 'opOrig' },
        { k: 'km', width: '120px', num: true },
    ].filter(c => rows.some(r => r[c.k] != null && r[c.k] !== ''));

    return {
        sub: t('repairs.sub', { count: rows.length }),
        template: `${columns.map(c => c.width).join(' ')}`,
        minWidth: columns.length * 130 + 120,
        head: columns.map(c => ({ label: t(`repairs.columns.${c.k}`), num: !!c.num })),
        rows: rows.map((row, i) => ({
            hidden: rows.length > REPAIR_LIMIT && i >= REPAIR_LIMIT && !allRepairs,
            cells: columns.map(c => ({
                value: cellValue(row[c.k], c.k),
                sub: c.sub ? row[c.sub] || '' : '',
                num: !!c.num,
                mono: !!c.mono,
                src: !!c.src,
            })),
        })),
        hasMore: rows.length > REPAIR_LIMIT,
        total: rows.length,
    };
};

// Equipment codes, with a group heading row before each group; past the first 10 they wait for "Show all"
export const buildEquipment = (equipment, { allCodes }, t) => {
    const codes = (equipment && equipment.codes) || [];
    if (!codes.length) return null;

    const big = codes.length > EQUIPMENT_LIMIT;
    const rows = [];
    let lastGroup = null;
    codes.forEach((code, i) => {
        const hidden = big && i >= EQUIPMENT_LIMIT && !allCodes;
        if (code.group && code.group !== lastGroup) {
            lastGroup = code.group;
            rows.push({ heading: code.group, hidden });
        }
        rows.push({ code: code.code, desc: code.desc || '', hidden });
    });

    return {
        sub: `${(equipment && equipment.source) || t('equipment.defaultSource')} · ${t('equipment.codes', { count: codes.length })}`,
        rows,
        big,
        total: codes.length,
    };
};

const stepState = (index, current) => {
    if (index < current) return 'done';
    return index === current ? 'cur' : '';
};

export const buildWaiting = (waiting, t) => {
    if (!waiting) return null;

    return {
        title: waiting.title || t('waiting.title'),
        requested: waiting.requested || '',
        waitLine: waiting.waitLine || '',
        note: waiting.note || '',
        steps: (waiting.steps || []).map((step, i) => ({
            n: i < waiting.current ? '✓' : String(i + 1),
            label: step.label,
            sub: step.sub || '',
            state: stepState(i, waiting.current),
        })),
    };
};

export const buildOutcome = (outcome, t) =>
    outcome
        ? {
              title: outcome.title,
              text: outcome.text || '',
              credit: outcome.credit || '',
              primary: outcome.primary || t('outcome.tryAgain'),
              secondary: outcome.secondary || t('outcome.contactSupport'),
              onPrimary: outcome.onPrimary,
              secondaryHref: outcome.secondaryHref,
          }
        : null;

export const buildOriginal = original =>
    original
        ? {
              title: original.title || '',
              text: original.text || '',
              tone: original.tone || 'info',
              file: original.file || '',
              url: original.url || '',
              optin: original.optin || '',
              onOptin: original.onOptin,
          }
        : null;

export const buildEmpty = empty =>
    empty
        ? {
              title: empty.title,
              text: empty.text,
              files: empty.files || [],
              actions: empty.actions !== false,
              onUpload: empty.onUpload,
              originalUrl: empty.originalUrl || '',
          }
        : null;

// Paint reading class: up to 160 µm original, 161-300 repainted, over 300 filler likely
export const paintClass = um => {
    if (um > 300) return 'alert';
    return um > 160 ? 'warn' : 'ok';
};

// The paint map is a top view of the car (icons/CarTopView.jsx, 800 x 400): front on the left, right side on top.
// Each place is where a panel's value sits, in those drawing units.
export const PAINT_PLACES = [
    { pos: 'frontBumper', side: 'Centre', x: 76, y: 200 },
    { pos: 'bonnet', side: 'Centre', x: 166, y: 200 },
    { pos: 'roof', side: 'Centre', x: 420, y: 200 },
    { pos: 'tailgate', side: 'Centre', x: 650, y: 200 },
    { pos: 'rearBumper', side: 'Centre', x: 724, y: 200 },
    { pos: 'frontWing', side: 'Right', x: 166, y: 80 },
    { pos: 'frontDoor', side: 'Right', x: 327, y: 80 },
    { pos: 'rearDoor', side: 'Right', x: 505, y: 80 },
    { pos: 'rearWing', side: 'Right', x: 648, y: 80 },
    { pos: 'frontWing', side: 'Left', x: 166, y: 320 },
    { pos: 'frontDoor', side: 'Left', x: 327, y: 320 },
    { pos: 'rearDoor', side: 'Left', x: 505, y: 320 },
    { pos: 'rearWing', side: 'Left', x: 648, y: 320 },
];

// Wide map: the car with room around it for the named values. Phone map: the car turned upright (front at the
// top, right side on the right), values only.
export const PAINT_WIDE_BOX = { x: -80, y: -80, w: 960, h: 560 };
export const PAINT_TALL_BOX = { x: -30, y: -10, w: 460, h: 820 };
const pct = (box, x, y) => ({ left: ((x - box.x) / box.w) * 100, top: ((y - box.y) / box.h) * 100 });

// "Right side" / "Left side": beside the named rows on the wide map, along the car on the phone map
const SIDE_LABELS = {
    wide: { right: pct(PAINT_WIDE_BOX, 20, -36), left: pct(PAINT_WIDE_BOX, 20, 436) },
    tall: { right: pct(PAINT_TALL_BOX, 407, 400), left: pct(PAINT_TALL_BOX, -12, 400) },
};

// Side panels are named above (right side) or below (left side) the car with a leader line, the bumpers beside
// it; the bonnet, roof and boot lid carry their name on the panel
const wideLabel = place => {
    if (place.side === 'Right') return { x: place.x, y: -36, lead: [place.x, -6, place.x, place.y] };
    if (place.side === 'Left') return { x: place.x, y: 436, lead: [place.x, 406, place.x, place.y] };
    if (place.pos === 'frontBumper') return { x: -22, y: 200, lead: [18, 200, place.x, 200] };
    if (place.pos === 'rearBumper') return { x: 822, y: 200, lead: [782, 200, place.x, 200] };
    return { x: place.x, y: place.y, lead: null };
};

const buildPaint = (paint, t) => {
    const panels = (paint && paint.panels) || [];
    const tags = {
        ok: ['paint.original', 'success'],
        warn: ['paint.repainted', 'warning'],
        alert: ['paint.filler', 'error'],
    };
    const places = PAINT_PLACES.map(place => {
        // two readings for one place (hood and bonnet, trunk and tailgate): the map shows the higher one
        const reading = panels
            .filter(p => p.pos === place.pos && (p.sideKey || p.side) === place.side)
            .reduce((top, p) => (!top || p.um > top.um ? p : top), null);
        const label = wideLabel(place);
        return {
            key: `${place.pos}-${place.side}`,
            pos: place.pos,
            side: place.side,
            name: reading ? reading.name : '',
            value: reading ? String(reading.um) : '',
            cls: reading ? paintClass(reading.um) : 'none',
            aria: reading
                ? `${reading.name}${place.side === 'Centre' ? '' : `, ${t(`adapter.sides.${place.side}`)}`}: ${withUnit(reading.um, 'um')}`
                : '',
            wide: pct(PAINT_WIDE_BOX, label.x, label.y),
            lead: label.lead,
            tall: pct(PAINT_TALL_BOX, 400 - place.y, place.x),
        };
    });
    const pins = places.filter(p => p.value);

    return {
        places,
        pins,
        sideLabels: SIDE_LABELS,
        // the map (and its hatched panels) is drawn only when something has a reading
        missing: pins.length > 0 && places.some(p => !p.value),
        mapAria: t('paint.mapAria', { readings: pins.map(p => p.aria).join(', ') }),
        panels: panels.map(p => ({
            name: p.name,
            side: p.side || '',
            um: withUnit(p.um, 'um'),
            tag: t(tags[paintClass(p.um)][0]),
            tagStyle: tags[paintClass(p.um)][1],
        })),
    };
};

const CHECK_STYLES = { Pass: 'success', Attention: 'warning', Fail: 'error' };
const SEVERITY_STYLES = { Minor: 'neutral', Moderate: 'warning', Major: 'error' };
const DOCUMENT_STYLES = { Checked: 'success', Missing: 'error' };

const buildChecks = (checks, t) => {
    const groups = [];
    (checks || []).forEach(check => {
        let group = groups.find(g => g.name === check.group);
        if (!group) groups.push((group = { name: check.group, items: [] }));
        group.items.push({
            name: check.name,
            // the English status picks the style; the label is translated
            status: check.status ? t(`labels.status.${check.status}`, { defaultValue: check.status }) : '',
            style: CHECK_STYLES[check.status] || 'neutral',
            note: check.note || '',
        });
    });
    // only checks with a result count; a reading such as a tyre's tread depth has none
    const rated = (checks || []).filter(c => c.status);
    const attention = rated.filter(c => c.status !== 'Pass').length;
    const verdict = attention ? t('inspection.attention', { count: attention }) : t('inspection.allPassed');
    const result = rated.length ? ` · ${verdict}` : '';
    const sub = t('inspection.checks', { count: (checks || []).length }) + result;

    return { groups, sub };
};

// Our own inspection of the car (internal report)
export const buildInspection = (inspection, t) => {
    if (!inspection) return null;

    const photos = inspection.photos || [];
    const damages = (inspection.damages || []).map(d => ({
        ...d,
        severity: d.severity ? t(`labels.severity.${d.severity}`, { defaultValue: d.severity }) : '',
        style: SEVERITY_STYLES[d.severity] || 'neutral',
    }));
    const checks = buildChecks(inspection.checks, t);

    return {
        photos,
        photoSub: t('inspection.photos', { count: photos.length }) + (inspection.date ? ` · ${inspection.date}` : ''),
        paint: buildPaint(inspection.paint, t),
        checkGroups: checks.groups,
        checkSub: checks.sub,
        damages,
        damageSub: t('inspection.damages', { count: damages.length }),
        documents: (inspection.documents || []).map(d => ({
            ...d,
            status: d.status ? t(`labels.status.${d.status}`, { defaultValue: d.status }) : '',
            note: d.note || '',
            style: DOCUMENT_STYLES[d.status] || 'warning',
        })),
        documentPhotos: inspection.documentPhotos || [],
        drive: inspection.drive || [],
        driveSub: inspection.driveSub || '',
        video: inspection.video || '',
        videoUrl: inspection.videoUrl || '',
    };
};
